/**
 * Which channels a requirement request may actually go out on (AC-4).
 *
 * The rule, per the PM's ruling on GDN-3226: the channels are the patient's
 * communication preferences INTERSECTED with the contact details the patient
 * actually has. A channel the patient asked us to avoid is skipped, and so is a
 * channel we have no address for. "Both" is simply what that intersection
 * produces for a patient with a mobile number, an email, and no avoid flags —
 * it is an outcome, never a default.
 *
 * Hardcoding both was the previous behaviour and was wrong in two directions:
 * it mailed patients who had asked us not to, and it claimed to text patients
 * we had no number for.
 *
 * TEMPORARY, and deliberately client-side. The backend does not derive channels
 * yet: `channels: []` is rejected by request validation (`min_length=1`) before
 * anything is recorded, so an empty set produces a 422 rather than the visible
 * delivery-history failure the PM's ruling requires. Until GDN-3356 lands —
 * server-side derivation, an explicit list as override, and a failed outbox row
 * with reason `no_eligible_channel` — the client derives the set and refuses to
 * call the endpoint with an empty one. When that ships and the contract
 * re-syncs, this whole module goes away and the caller stops passing channels.
 *
 * A channel the patient HAS no contact for already fails visibly in delivery
 * history today, so the gap is only the empty-set case.
 */

/** The channels the send endpoint accepts. */
export type SendChannel = 'sms' | 'email';

/** 'avoid' is the only value that excludes; absent means no preference stated. */
type ContactMethodPreference = 'preferred' | 'avoid' | 'neutral';

export interface ChannelContact {
  email?: string | null;
  mobile_phone?: string | null;
  communication_preferences?: {
    email?: ContactMethodPreference;
    sms?: ContactMethodPreference;
  };
}

const hasValue = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Channels to attempt, in a stable order. Empty means there is no way to reach
 * this patient — the caller must NOT send with an empty list; see
 * `sendBlockedReason`.
 */
export const deriveSendChannels = (
  contact: ChannelContact | undefined,
): SendChannel[] => {
  if (!contact) return [];
  const preferences = contact.communication_preferences ?? {};

  const channels: SendChannel[] = [];
  // Only an explicit 'avoid' excludes a channel. An unstated preference is not
  // consent to be silent — it means the patient never expressed one.
  if (hasValue(contact.mobile_phone) && preferences.sms !== 'avoid') {
    channels.push('sms');
  }
  if (hasValue(contact.email) && preferences.email !== 'avoid') {
    channels.push('email');
  }
  return channels;
};

/**
 * Why sending is unavailable, phrased so a staff member knows what to fix.
 * Returns undefined when at least one channel qualifies.
 *
 * The two causes are deliberately distinguished: "no contact details" and "the
 * patient asked us not to" call for opposite responses, and collapsing them
 * would send staff to edit a field that is already correct.
 */
export const sendBlockedReason = (
  contact: ChannelContact | undefined,
  subjectSingular: string,
): string | undefined => {
  if (deriveSendChannels(contact).length > 0) return undefined;

  const preferences = contact?.communication_preferences ?? {};
  const hasAnyAddress = hasValue(contact?.mobile_phone) || hasValue(contact?.email);
  const everyAddressAvoided =
    hasAnyAddress &&
    (!hasValue(contact?.mobile_phone) || preferences.sms === 'avoid') &&
    (!hasValue(contact?.email) || preferences.email === 'avoid');

  if (everyAddressAvoided) {
    return `This ${subjectSingular} has asked not to be contacted on the channels we have. Update their communication preferences to send a request.`;
  }
  return `No mobile number or email on file for this ${subjectSingular}. Add one to send a request.`;
};
